import React, { useContext, useEffect, useMemo, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import { LanguageContext } from "./utils/LanguageProvider";
import { useServer } from "./context/ServerContext";
import { useAutoRefresh } from "./utils/ApiLogin";

import baseTheme from "./components/Theme/theme";

import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Registro from "./pages/Registro";
import CreateCenters from "./pages/CreateCenters";
import ConfigMenu from "./pages/ConfigMenu";
import Home from "./pages/Home";
import Usuarios from "./pages/Usuarios";

import MainEnviro from "./services/Enviro/MainEnviro";
import MainNanoox from "./services/Nanoox/MainNanoox";
import MainNanooxBolt from "./services/NanooxBolt/MainNanooxBolt";
import MainPSS from "./services/PSS/MainPSS";
import SLA from "./services/PSS/pages/SLA";
import MainScapp from "./services/Scaap/MainScapp";
import LabelsProvider from "./features/labels/LabelsProvider";
import AssistantProvider from "./features/ai-assistant/AssistantProvider";

import { SW_VERSION as SWVersion } from "./version";

const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    mode: "dark",
    background: {
      // default: "#000",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background:
          "radial-gradient(100% 100% at 50% 0%, rgba(46, 179, 238, 0.08) 0%, rgba(0, 0, 0, 0) 70%), linear-gradient(180deg, rgb(10, 20, 35) 0%, rgb(3, 8, 18) 100%)",
            // "radial-gradient(100% 100% at 50% 0%, rgba(46, 179, 238, 0.08) 0%, rgba(0, 0, 0, 0) 70%), linear-gradient(180deg, rgb(10, 20, 35) 0%, rgb(3, 8, 18) 100%)",
          // boxShadow:
            // "inset 0 1px 0 rgba(255,255,255,0.04), 0 6px 20px rgba(0,0,0,0.4)",
          color: "#EDEFF3",
        },
      },
    },
  },
});

function App() {
  const { servidor, aiWebhookUrl, aiAssistantEnabled } = useServer();
  const navigate = useNavigate();
  const location = useLocation();
  const { translate, getTranslate, changeLanguage, language } =
    useContext(LanguageContext);

  const [sessionExpired, setSessionExpired] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [registro, setRegistro] = useState(false);
  const [openMenuIdioma, setOpenMenuIdioma] = useState(null);
  const [cargo, setCargo] = useState(localStorage.getItem("cargo"));
  const [otherWindowsIndex, setOtherWindowsIndex] = useState(0);

  const idioma = language;

  const sharedTranslate = translate;
  const nanooxTranslate = useMemo(() => getTranslate("nanoox"), [getTranslate]);
  const nanooxBoltTranslate = useMemo(
    () => getTranslate("nanooxBolt"),
    [getTranslate]
  );
  const enviroTranslate = useMemo(() => getTranslate("enviro"), [getTranslate]);
  const pssTranslate = useMemo(() => getTranslate("pss"), [getTranslate]);
  const scappTranslate = useMemo(() => getTranslate("scapp"), [getTranslate]);
  const publicPaths = [
    "/",
    "/ResetPassword",
    "/Registro",
    "/Usuarios",
    "/CreateCenters",
  ];
  const isPublicPath = publicPaths.some((path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path)
  );
  const shouldAutoRefresh =
    !isPublicPath && Boolean(localStorage.getItem("username"));

  useAutoRefresh(servidor, () => {
    setTimeout(() => {
      setSessionExpired(true);
    }, 5000);
  }, shouldAutoRefresh);

  const handleLanguageChange = (newLanguage) => {
    changeLanguage(newLanguage);
    setOpenMenuIdioma(null);
  };

  const handleClick = (event) => {
    setOpenMenuIdioma(event.currentTarget);
  };

  const handleCloseIdioma = () => {
    setOpenMenuIdioma(null);
  };

  useEffect(() => {
    if (!servidor) return;

    const maybeDomain = servidor.split("//")?.[1]?.split(".")?.[0];
    const domainUpper = maybeDomain ? maybeDomain.toUpperCase() : null;
    document.title = domainUpper
      ? `ITG Service Manager - ${domainUpper}`
      : "ITG Service Manager";
  }, [servidor]);

  const baseProps = {
    handleClick,
    openMenuIdioma,
    handleCloseIdioma,
    handleLanguageChange,
    idioma,
  };

  const LoginProps = {
    SWVersion,
    servidor,
    translate: sharedTranslate,
    ...baseProps,
  };

  const RegistroProps = {
    translate: sharedTranslate,
    cargo,
    ...baseProps,
  };

  const CreateCentersProps = {
    translate: sharedTranslate,
    ...baseProps,
  };

  const HomeProps = {
    SWVersion,
    registro,
    translate: sharedTranslate,
    cargo,
    servidor,
    selectedRegion,
    setSelectedRegion,
    selectedArea,
    setSelectedArea,
    otherWindowsIndex,
    setOtherWindowsIndex,
    ...baseProps,
  };

  const routeSharedProps = {
    selectedRegion,
    selectedArea,
    setSelectedRegion,
    setSelectedArea,
    otherWindowsIndex,
    setOtherWindowsIndex,
    ...baseProps,
  };

  const NanooxProps = {
    translate: nanooxTranslate,
    ...routeSharedProps,
  };

  const NanooxBoltProps = {
    translate: nanooxBoltTranslate,
    ...routeSharedProps,
  };

  const EnviroProps = {
    translate: enviroTranslate,
    ...routeSharedProps,
  };

  const PSSProps = {
    translate: pssTranslate,
    ...routeSharedProps,
  };

  const ScappProps = {
    translate: scappTranslate,
    ...routeSharedProps,
  };

  return (
    <div style={{ userSelect: "none" }}>
      {location.pathname !== "/" && (
        <Dialog
          open={sessionExpired}
          disableEscapeKeyDown
          aria-labelledby="expired-session-title"
          PaperProps={{
            sx: {
              bgcolor: "#13131f",
              borderRadius: "1vw",
              p: "1vw",
              minWidth: "10vw",
            },
          }}
        >
          <DialogTitle
            id="expired-session-title"
            sx={{
              color: "#e84e0e",
              fontWeight: 700,
              fontSize: "1.25vw",
              textAlign: "center",
            }}
          >
            Sesión expirada
          </DialogTitle>
          <DialogContent
            sx={{
              color: "#E0E0E0",
              textAlign: "center",
              py: "1vh",
              fontSize: "1vw",
            }}
          >
            Tu sesión ha caducado. Vuelve a iniciar sesión.
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", py: 2 }}>
            <Button
              onClick={() => {
                navigate("/");
                setSessionExpired(false);
              }}
              variant="contained"
              sx={{
                color: "#E0E0E0",
                fontWeight: 600,
                textTransform: "none",
                px: "1vw",
                py: "1vh",
                borderRadius: "0.9vw",
                boxShadow: "0px 4px 10px rgba(0,0,0,0.3)",
                "&:hover": { bgcolor: "#303e54" },
                bgcolor: "#e84e0e",
              }}
            >
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {servidor && (
        <ThemeProvider theme={darkTheme}>
          <CssBaseline />
          <LabelsProvider server={servidor}>
          <AssistantProvider
            aiWebhookUrl={aiWebhookUrl}
            aiAssistantEnabled={aiAssistantEnabled}
            appVersion={SWVersion}
            serverUrl={servidor}
          >
            <Routes>
              <Route path="/" element={<Login {...LoginProps} />} />
              <Route
                path="/ResetPassword/:token"
                element={<ResetPassword {...LoginProps} />}
              />
              <Route path="/home" element={<Home {...HomeProps} />} />
              <Route path="/Registro" element={<Registro {...RegistroProps} />} />
              <Route path="/config/:tab" element={<ConfigMenu {...RegistroProps} />} />
              <Route path="/Usuarios/:token" element={<Usuarios />} />
              <Route
                path="/CreateCenters/:token"
                element={<CreateCenters {...CreateCentersProps} />}
              />

              <Route
                path="/Enviro/:region?/:area?/:centro?"
                element={<MainEnviro {...EnviroProps} />}
              />
              <Route
                path="/Nanoox/:region?/:area?/:centro?"
                element={<MainNanoox {...NanooxProps} />}
              />
              <Route
                path="/Flux/:region?/:area?/:centro?"
                element={<MainNanooxBolt {...NanooxBoltProps} />}
              />
              <Route
                path="/PSS/:region?/:area?/:centro?"
                element={<MainPSS {...PSSProps} />}
              />
              <Route
                path="/PSS Servicio SLA/:region?/:area?/:centro?"
                element={<SLA {...PSSProps} />}
              />
              <Route
                path="/Scapp/:region?/:area?/:centro?"
                element={<MainScapp {...ScappProps} />}
              />
            </Routes>
          </AssistantProvider>
          </LabelsProvider>
        </ThemeProvider>
      )}
    </div>
  );
}

export default App;
